import { DOCUMENT } from "@angular/common";
import { computed, inject, Injectable, signal, type Signal, type WritableSignal } from "@angular/core";
import { BeforeInstallPromptEvent } from "@core/models/pwa/before-install-prompt-event.model";

export type PwaInstallState = "installed" | "prompt" | "ios" | "unsupported";

@Injectable({
  providedIn: "root",
})
export class PwaInstallService {
  private readonly window: Window | null = inject(DOCUMENT).defaultView;
  private deferredPrompt: BeforeInstallPromptEvent | null = null;

  private readonly _canPrompt: WritableSignal<boolean> = signal<boolean>(false);
  private readonly _installed: WritableSignal<boolean> = signal<boolean>(this.isStandalone());

  readonly state: Signal<PwaInstallState> = computed((): PwaInstallState => {
    if (this._installed()) return "installed";
    if (this._canPrompt()) return "prompt";
    if (this.isIosSafari()) return "ios";
    return "unsupported";
  });

  readonly canOffer: Signal<boolean> = computed((): boolean => this.state() === "prompt" || this.state() === "ios");

  constructor() {
    this.window?.addEventListener("beforeinstallprompt", (event: Event): void => {
      // web.dev/learn/pwa/installation-prompt: la instalación la ofrece nuestro botón, no el aviso del navegador.
      event.preventDefault();
      this.deferredPrompt = event as BeforeInstallPromptEvent;
      this._canPrompt.set(true);
    });

    this.window?.addEventListener("appinstalled", (): void => {
      this.deferredPrompt = null;
      this._canPrompt.set(false);
      this._installed.set(true);
    });
  }

  async install(): Promise<void> {
    const promptEvent: BeforeInstallPromptEvent | null = this.deferredPrompt;
    if (!promptEvent) return;

    await promptEvent.prompt();
    const { outcome } = await promptEvent.userChoice;

    this.deferredPrompt = null;
    this._canPrompt.set(false);
    if (outcome === "accepted") this._installed.set(true);
  }

  private isStandalone(): boolean {
    if (!this.window) return false;

    const iosStandalone: boolean = (this.window.navigator as Navigator & { standalone?: boolean }).standalone === true;
    return this.window.matchMedia("(display-mode: standalone)").matches || iosStandalone;
  }

  // web.dev/learn/pwa/installation-prompt: en iOS solo Safari instala, desde Compartir.
  // ponytail: detección por user agent; otro navegador de iOS con "Version/" y sin sigla propia pasaría como Safari.
  private isIosSafari(): boolean {
    if (!this.window) return false;

    const { userAgent, maxTouchPoints } = this.window.navigator;
    const isIos: boolean = /iphone|ipad|ipod/i.test(userAgent) || (/macintosh/i.test(userAgent) && maxTouchPoints > 1);
    return isIos && /Version\/.*Safari/.test(userAgent) && !/CriOS|FxiOS|EdgiOS|OPiOS/.test(userAgent);
  }
}
